import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Monitor, Package, Car, Building2, Sofa, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPrice } from '@/lib/utils';
import { apiRequest, getSessionId } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import logger from '@/utils/logger';

interface MediaOwner {
  id: string;
  name: string;
  logo: string;
  bannerImage: string;
  tagline: string;
  rating: number;
  reviewCount: number;
  followers: number;
  itemsSold: number;
  joinedDate: string;
  responseTime: string;
  location: string;
  description: string;
  verified: boolean;
  brandColor: string;
  specialties: string[];
}

interface MediaOwnerHomeProps {
  mediaOwner: MediaOwner;
}

export function MediaOwnerHome({ mediaOwner }: MediaOwnerHomeProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Fetch actual inventory for this media owner
  const { data: allInventory, isLoading, error } = useQuery({
    queryKey: ['/api/billboards'],
  });

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async (data: { billboardId: number; duration: string }) => {
      return apiRequest("POST", "/api/cart", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cart"] });
      toast({
        title: "Added to cart",
        description: "Billboard has been added to your cart successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add billboard to cart. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAddToCart = (billboardId: number) => {
    const sessionId = getSessionId();
    addToCartMutation.mutate({
      billboardId,
      duration: "1-month",
      sessionId,
    });
  };

  logger.log('MediaOwnerHome Debug:', {
    mediaOwnerId: mediaOwner.id,
    allInventoryCount: Array.isArray(allInventory) ? allInventory.length : 0,
    filteredCount: Array.isArray(allInventory) ? allInventory.filter((item: any) => item.mediaOwner === mediaOwner.id).length : 0
  });

  // Filter inventory by this media owner
  const mediaOwnerInventory = Array.isArray(allInventory) ? allInventory.filter((item: any) => 
    item.mediaOwner === mediaOwner.id
  ) : [];

  // Categorize inventory for different sections
  const newInventory = mediaOwnerInventory.filter((item: any) => {
    const createdDate = new Date(item.createdAt || '2024-12-01');
    const now = new Date();
    const daysDiff = (now.getTime() - createdDate.getTime()) / (1000 * 3600 * 24);
    return daysDiff <= 30; // New within last 30 days
  }).slice(0, 2);

  const topPerformingInventory = mediaOwnerInventory.filter((item: any) => item.viewCount >= 800000).slice(0, 2);
  const millionViewInventory = mediaOwnerInventory.filter((item: any) => item.viewCount >= 1000000).slice(0, 2);

  // Get featured inventory for landing cards (combining different categories)
  // Use Set to remove duplicates and ensure unique keys
  const allCombinedInventory = [
    ...newInventory.map((item: any) => ({ ...item, specialTag: 'New', uniqueKey: `new-${item.id}` })),
    ...topPerformingInventory.map((item: any) => ({ ...item, specialTag: 'Best Seller', uniqueKey: `best-${item.id}` })),
    ...millionViewInventory.map((item: any) => ({ ...item, specialTag: '1M+ Views', uniqueKey: `million-${item.id}` }))
  ];
  
  // Remove duplicates based on ID
  const uniqueInventoryMap = new Map();
  allCombinedInventory.forEach(item => {
    if (!uniqueInventoryMap.has(item.id)) {
      uniqueInventoryMap.set(item.id, item);
    }
  });
  
  const featuredInventory = Array.from(uniqueInventoryMap.values()).slice(0, 6).map((item: any) => ({
    id: item.id,
    uniqueKey: item.uniqueKey,
    title: item.title,
    location: item.location,
    price: item.price,
    image: item.imageUrl,
    tag: item.isAvailable ? "Available" : "Sold Out",
    specialTag: item.specialTag,
    type: item.inventoryType === 'digital' ? 'Digital' : 
          item.inventoryType === 'static' ? 'Classic' : 
          item.inventoryType === 'Package' ? 'Package' : 'Transit',
    description: item.description || "Premium advertising space in high-traffic location",
    viewCount: item.viewCount || 0
  }));

  // Define categories with inventory counts
  const inventoryByType = mediaOwnerInventory.reduce((acc: any, item: any) => {
    const type = item.inventoryType === 'digital' ? 'Digital' : 
                 item.inventoryType === 'static' ? 'Classic' : 
                 item.inventoryType === 'Package' ? 'Package' : 'Transit';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  const categories = [
    {
      name: 'Digital',
      icon: Monitor,
      image: inventoryByType.Digital?.[0]?.imageUrl || "https://images.unsplash.com/photo-1551677188-3dbb4e62a4d8?w=300&h=200&fit=crop",
      count: inventoryByType.Digital?.length || 0
    },
    {
      name: 'Classic',
      icon: Building2,
      image: inventoryByType.Classic?.[0]?.imageUrl || "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=300&h=200&fit=crop",
      count: inventoryByType.Classic?.length || 0
    },
    {
      name: 'Package',
      icon: Package,
      image: inventoryByType.Package?.[0]?.imageUrl || "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=300&h=200&fit=crop",
      count: inventoryByType.Package?.length || 0
    },
    {
      name: 'Transit',
      icon: Car,
      image: inventoryByType.Transit?.[0]?.imageUrl || "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=300&h=200&fit=crop",
      count: inventoryByType.Transit?.length || 0
    },
    {
      name: 'Fleet',
      icon: Car,
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=300&h=200&fit=crop",
      count: 0
    },
    {
      name: 'Street Furniture',
      icon: Sofa,
      image: "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=300&h=200&fit=crop",
      count: 0
    }
  ];

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Loading inventories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading inventories</p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* About Us Section */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">About Us</h2>
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-4">
            {mediaOwner.description}
          </p>
          <div className="grid md:grid-cols-2 gap-8 mt-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Specialties</h3>
              <ul className="space-y-2">
                {mediaOwner.specialties.map((specialty, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                    {specialty}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Why Choose Us</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                  {mediaOwner.responseTime}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                  {mediaOwner.itemsSold.toLocaleString()}+ successful campaigns delivered
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                  Active since {mediaOwner.joinedDate}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></span>
                  {mediaOwner.rating}/5.0 customer satisfaction rating
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Inventory Sections */}
      {newInventory.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">🆕 New Inventory</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {newInventory.map((item: any, index: number) => (
              <Card key={`new-${item.id}-${index}`} className="relative overflow-hidden h-80 hover:shadow-lg transition-shadow group cursor-pointer">
                <div 
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${item.imageUrl})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
                </div>
                
                <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
                  <div className="flex justify-between items-start">
                    <Badge className="bg-blue-600 text-white border-0 animate-pulse">
                      NEW
                    </Badge>
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      {item.inventoryType === 'digital' ? 'Digital' : item.inventoryType === 'static' ? 'Classic' : 'Package'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-sm opacity-90 mb-3">{item.description}</p>
                      <div className="flex items-center text-sm opacity-75">
                        <MapPin className="h-4 w-4 mr-1" />
                        {item.location}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">
                        {formatPrice(item.price)}
                      </div>
                      <div className="flex space-x-2">
                        <Link href={`/billboard/${item.id}?from=store&ownerId=${mediaOwner.id}`}>
                          <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                            View Details
                          </Button>
                        </Link>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAddToCart(item.id)}
                          disabled={!item.isAvailable || addToCartMutation.isPending}
                        >
                          {addToCartMutation.isPending ? 'Adding...' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Top Performance / Best Sellers */}
      {topPerformingInventory.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">🏆 Top Performance / Best Sellers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {topPerformingInventory.map((item: any, index: number) => (
              <Card key={`top-${item.id}-${index}`} className="relative overflow-hidden h-80 hover:shadow-lg transition-shadow group cursor-pointer">
                <div 
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${item.imageUrl})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
                </div>
                
                <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
                  <div className="flex justify-between items-start">
                    <Badge className="bg-amber-600 text-white border-0">
                      BEST SELLER
                    </Badge>
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      {item.inventoryType === 'digital' ? 'Digital' : item.inventoryType === 'static' ? 'Classic' : 'Package'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-sm opacity-90 mb-3">{item.description}</p>
                      <div className="flex items-center text-sm opacity-75 mb-2">
                        <MapPin className="h-4 w-4 mr-1" />
                        {item.location}
                      </div>
                      <div className="text-sm opacity-75">
                        📊 {(item.viewCount || 0).toLocaleString()} views/month
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">
                        {formatPrice(item.price)}
                      </div>
                      <div className="flex space-x-2">
                        <Link href={`/billboard/${item.id}?from=store&ownerId=${mediaOwner.id}`}>
                          <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                            View Details
                          </Button>
                        </Link>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAddToCart(item.id)}
                          disabled={!item.isAvailable || addToCartMutation.isPending}
                        >
                          {addToCartMutation.isPending ? 'Adding...' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Over 1 Million Views */}
      {millionViewInventory.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">🚀 Over 1 Million Views</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {millionViewInventory.map((item: any, index: number) => (
              <Card key={`million-${item.id}-${index}`} className="relative overflow-hidden h-80 hover:shadow-lg transition-shadow group cursor-pointer">
                <div 
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${item.imageUrl})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
                </div>
                
                <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
                  <div className="flex justify-between items-start">
                    <Badge className="bg-purple-600 text-white border-0">
                      1M+ VIEWS
                    </Badge>
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                      {item.inventoryType === 'digital' ? 'Digital' : item.inventoryType === 'static' ? 'Classic' : 'Package'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-sm opacity-90 mb-3">{item.description}</p>
                      <div className="flex items-center text-sm opacity-75 mb-2">
                        <MapPin className="h-4 w-4 mr-1" />
                        {item.location}
                      </div>
                      <div className="text-sm opacity-75 font-semibold">
                        🔥 {(item.viewCount || 0).toLocaleString()} views/month
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">
                        {formatPrice(item.price)}
                      </div>
                      <div className="flex space-x-2">
                        <Link href={`/billboard/${item.id}?from=store&ownerId=${mediaOwner.id}`}>
                          <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                            View Details
                          </Button>
                        </Link>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAddToCart(item.id)}
                          disabled={!item.isAvailable || addToCartMutation.isPending}
                        >
                          {addToCartMutation.isPending ? 'Adding...' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Storefront Landing Section - Remaining Featured Cards */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Inventory</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredInventory.slice(0, 4).map((item: any, index: number) => (
            <Card key={item.uniqueKey || `featured-${item.id}-${index}`} className="relative overflow-hidden h-80 hover:shadow-lg transition-shadow group cursor-pointer">
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${item.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
            </div>
            
            <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
              <div className="flex justify-between items-start">
                <div className="flex space-x-2">
                  <Badge className="bg-emerald-600 text-white border-0">
                    {item.tag}
                  </Badge>
                  {item.specialTag && (
                    <Badge className={`text-white border-0 ${
                      item.specialTag === 'New' ? 'bg-blue-600' :
                      item.specialTag === 'Best Seller' ? 'bg-amber-600' :
                      item.specialTag === '1M+ Views' ? 'bg-purple-600' : 'bg-gray-600'
                    }`}>
                      {item.specialTag}
                    </Badge>
                  )}
                </div>
                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                  {item.type}
                </Badge>
              </div>
              
              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-90 mb-3">{item.description}</p>
                  <div className="flex items-center text-sm opacity-75 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    {item.location}
                  </div>
                  {item.viewCount > 0 && (
                    <div className="text-sm opacity-75">
                      📊 {item.viewCount.toLocaleString()} views/month
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">
                    {formatPrice(item.price)}
                  </div>
                  <div className="flex space-x-2">
                    <Link href={`/billboard/${item.id}?from=store&ownerId=${mediaOwner.id}`}>
                      <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                        View Details
                      </Button>
                    </Link>
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleAddToCart(item.id)}
                      disabled={!item.isAvailable || addToCartMutation.isPending}
                    >
                      {addToCartMutation.isPending ? 'Adding...' : 'Add to Cart'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          ))}
        </div>
      </div>

      {/* Find Exactly what you're looking for - Categories Section */}
      <div className="mt-12">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
          Find Exactly what you're looking for
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Card key={category.name} className="relative overflow-hidden h-48 hover:shadow-lg transition-all duration-300 group cursor-pointer">
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${category.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60 group-hover:from-black/30 group-hover:via-black/40 group-hover:to-black/70 transition-all duration-300" />
              </div>
              
              <div className="relative z-10 p-6 h-full flex flex-col justify-between text-white">
                <div className="flex justify-center">
                  <category.icon className="h-12 w-12 text-white opacity-80" />
                </div>
                
                <div className="text-center">
                  <h3 className="text-xl font-bold mb-1">{category.name}</h3>
                  <p className="text-sm opacity-75">
                    {category.count > 0 ? `${category.count} available` : 'Coming soon'}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}